import { Flex } from 'components/shared/flex'
import { usePostSalesAgencyApi, usePutSalesAgencyApi } from 'api/sales_agency'
import { DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { SalesAgency, SalesAgencyForm } from 'entities/sales_agency'

export type FormViewProps = {
    form: Form<SalesAgencyForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    return (
        <>
            <Flex>
                <InputField form={form} attr="name" apiError={apiError} label="名称" required />
                <InputField form={form} attr="email" apiError={apiError} label="メールアドレス" required placeholder="xxx@xxx.com" />
            </Flex>
            <Flex>
                <DateField form={form} attr="contractDate" apiError={apiError} label="契約締結日" required />
                <SelectField
                    form={form}
                    attr="expirationType"
                    apiError={apiError}
                    label="有効期限タイプ"
                    selectItems={[
                        { label: '営業代行の契約日基準', value: 'absolute' },
                        { label: '医療機関の契約日基準', value: 'relative' },
                    ]}
                    includeBlank
                    required
                />
            </Flex>
            <Flex>
                <InputField
                    form={form}
                    attr="expirationYear"
                    apiError={apiError}
                    label="有効期限(年)"
                    required
                    type="number"
                    min={1}
                    max={50}
                    placeholder={'2'}
                />
                <InputField
                    form={form}
                    attr="expirationMonth"
                    apiError={apiError}
                    label="有効期限(月)"
                    required
                    type="number"
                    min={0}
                    max={50}
                    placeholder={'0'}
                />
            </Flex>
            <Flex>
                <InputField form={form} attr="acquisitionReward" apiError={apiError} label="契約締結報酬" required type="number" min={0} placeholder="100000" />
                <InputField
                    form={form}
                    attr="salesMargin"
                    apiError={apiError}
                    label="売上マージン率"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    required
                    placeholder="0.05"
                />
            </Flex>
        </>
    )
}

type NewSalesAgencyFormProps = {
    onComplete?: () => void
}

export const NewSalesAgencyForm = (props: NewSalesAgencyFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostSalesAgencyApi()
    const form = useForm<SalesAgencyForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>営業代行の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditSalesAgencyFormProps = {
    salesAgency: SalesAgency
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditSalesAgencyForm = (props: EditSalesAgencyFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutSalesAgencyApi()
    const form = useForm<SalesAgencyForm>(props.salesAgency)

    useEffectSkipFirst(() => {
        form.set(props.salesAgency)
    }, [props.salesAgency])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>営業代行の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
